import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookConsultModal from '../components/BookConsultModal';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, Shield, Cloud, Users, CheckCircle, AlertCircle } from 'lucide-react';

const Microsoft365Page = () => {
  const [showModal, setShowModal] = useState(false);

  const services = [
    {
      id: 'migration',
      icon: Cloud,
      title: 'Migration & Setup',
      features: [
        { id: 'mig-1', text: 'Complete email migration from any platform' },
        { id: 'mig-2', text: 'User account setup and configuration' },
        { id: 'mig-3', text: 'Calendar and contact migration' },
        { id: 'mig-4', text: 'User training and documentation' }
      ]
    },
    {
      id: 'security',
      icon: Shield,
      title: 'Security',
      features: [
        { id: 'sec-1', text: 'Multi-factor authentication (MFA) setup' },
        { id: 'sec-2', text: 'Conditional access policies' },
        { id: 'sec-3', text: 'Security defaults and baselines' },
        { id: 'sec-4', text: 'Admin account protection' }
      ]
    },
    {
      id: 'sharepoint',
      icon: Cloud,
      title: 'SharePoint & OneDrive',
      features: [
        { id: 'sp-1', text: 'SharePoint site structure and setup' },
        { id: 'sp-2', text: 'Permissions cleanup and governance' },
        { id: 'sp-3', text: 'File migration from network drives' },
        { id: 'sp-4', text: 'Sharing policies and external access' }
      ]
    },
    {
      id: 'teams',
      icon: Users,
      title: 'Teams & Collaboration',
      features: [
        { id: 'tm-1', text: 'Teams setup and configuration' },
        { id: 'tm-2', text: 'External sharing policies' },
        { id: 'tm-3', text: 'Guest access management' },
        { id: 'tm-4', text: 'Meeting policies and settings' }
      ]
    }
  ];

  const ongoingServices = [
    { id: 'ongoing-admin', title: 'User Administration', desc: 'Add/remove users, reset passwords, manage licenses' },
    { id: 'ongoing-security', title: 'Security Monitoring', desc: 'Review sign-in logs, investigate alerts, maintain policies' },
    { id: 'ongoing-optimize', title: 'Optimization', desc: 'License optimization, feature rollouts, best practices' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-cyan-500 uppercase tracking-wide mb-2">Microsoft 365 Specialists</p>
            <h1 className="text-5xl font-bold text-gray-900 mb-4">Microsoft 365 Done Right</h1>
            <p className="text-xl text-gray-600 mb-8">
              Migration, security, and ongoing management for organizations with 1–50 users.
            </p>
            <div className="flex justify-center gap-4">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowModal(true)} data-testid="m365-hero-consult">
                <Calendar className="w-5 h-5 mr-2" />
                Book a Free Consult
              </Button>
            </div>
            <p className="text-sm text-gray-600 mt-4">
              We respond within 1 business day (often faster). Emergency support available.
            </p>
          </div>
          <div className="mt-12">
            <img
              src="https://images.pexels.com/photos/7689856/pexels-photo-7689856.jpeg?auto=compress&cs=tinysrgb&w=800"
              alt="IT professionals collaborating"
              className="w-full h-96 object-cover rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">What We Do</h2>
          <p className="text-lg text-gray-600 text-center mb-12">
            Complete Microsoft 365 services from migration to ongoing management
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Card key={service.id} className="border-2 border-gray-200">
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {service.features.map((feature) => (
                        <li key={feature.id} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{feature.text}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ongoing Management */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Ongoing Management</h2>
          <p className="text-lg text-gray-600 text-center mb-12">
            We don't just set it up and leave—we keep it running smoothly
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {ongoingServices.map((service) => (
              <Card key={service.id} className="border border-gray-200">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-8">
            <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-2">Case Study</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Nonprofit Microsoft 365 Cleanup + Security Baseline</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  The Problem:
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  A 25-user nonprofit had sharing sprawl across SharePoint, risky login settings, and no MFA. Staff were confused about where to find files, and external sharing was uncontrolled.
                </p>
              </div>
              
              <div>
                <h3 className="font-bold text-gray-900 mb-2">What We Did:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                    <span className="text-gray-600">Enforced MFA for all users</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                    <span className="text-gray-600">Implemented conditional access policies</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                    <span className="text-gray-600">Cleaned up SharePoint permissions</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                    <span className="text-gray-600">Established device policy baseline</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-bold text-gray-900 mb-2">The Outcome:</h3>
                <p className="text-gray-600 leading-relaxed">
                  Reduced account risk significantly, improved collaboration with clear file organization, and simplified access for staff. The organization now has a security posture appropriate for handling donor data.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Get Microsoft 365 Right?</h2>
          <p className="text-xl text-blue-50 mb-8">
            Let's discuss your Microsoft 365 needs and how we can help.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100" onClick={() => setShowModal(true)} data-testid="m365-cta-consult">
              <Calendar className="w-5 h-5 mr-2" />
              Book a Free Consult
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <BookConsultModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};

export default Microsoft365Page;
